import { useMemo, useState } from 'react';
import type { Capture, CaptureType } from '@daily/shared';
import { ArtCard } from './ArtCard';
import { Lightbox } from '@/components/shared/Lightbox';
import clsx from 'clsx';

interface GalleryWallProps {
  captures: Capture[];
}

const FILTERS: { id: 'ALL' | CaptureType; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'DRAWING', label: 'Drawings' },
  { id: 'SIENA_WRITING', label: "Siena's writing" },
  { id: 'LETTER', label: 'Letters' },
  { id: 'INVITE', label: 'Invites' },
  { id: 'CARD', label: 'Cards' },
  { id: 'CERTIFICATE', label: 'Certificates' },
  { id: 'FLYER', label: 'Flyers' },
];

export function GalleryWall({ captures }: GalleryWallProps) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('ALL');
  const [lightbox, setLightbox] = useState<Capture | null>(null);

  const filtered = useMemo(
    () => (filter === 'ALL' ? captures : captures.filter((c) => c.captureType === filter)),
    [captures, filter],
  );

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3 max-w-2xl">
        <span className="text-eyebrow uppercase tracking-[0.18em] text-tomato">
          The Wall Keeps Its Pictures
        </span>
        <h1 className="font-serif text-display-lg leading-tight">
          Nothing is reduced to <em className="text-tomato">metadata.</em>
        </h1>
        <p className="font-serif italic text-body-lg text-inkSoft leading-snug">
          Every letter, invite, card and drawing keeps its picture. The Daily reads the words
          and pins the artwork. Six years from now this wall is a museum of small things.
        </p>
      </header>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={clsx(
              'px-3.5 py-1.5 rounded-full text-body-sm font-medium transition-colors',
              filter === f.id
                ? 'bg-tomato text-white'
                : 'bg-card text-inkSoft hover:bg-cardWarm',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        className="rounded-xl py-12 px-8 relative overflow-hidden shadow-inner"
        style={{
          background: 'linear-gradient(135deg, #FBF2DF 0%, #F4E4C7 100%)',
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(122,111,95,0.08) 1.5px, transparent 1.5px), radial-gradient(rgba(122,111,95,0.06) 1px, transparent 1px)',
            backgroundSize: '12px 12px, 7px 7px',
            backgroundPosition: '0 0, 3px 4px',
          }}
        />
        <div className="relative z-[2] grid gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {filtered.length === 0 ? (
            <p className="col-span-full font-serif italic text-inkFaded text-center py-8">
              The wall is quiet for that one.
            </p>
          ) : (
            filtered.map((c, i) => (
              <ArtCard
                key={c.id}
                capture={c}
                index={i}
                onClick={c.thumbnailUrl ? () => setLightbox(c) : undefined}
              />
            ))
          )}
        </div>
      </div>

      {lightbox?.thumbnailUrl && (
        <Lightbox
          src={lightbox.thumbnailUrl}
          caption={lightbox.caption ?? undefined}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
